/**
 * Centralized error message parser
 * Converts database and API errors into user-friendly messages
 */

interface ParsedError {
  message: string;
  field?: string;
  isRetryable: boolean;
}

// Helper to format field names for display
function formatFieldName(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

// Database constraint error patterns
const DB_ERROR_PATTERNS: Array<{
  pattern: RegExp;
  getMessage: (match: RegExpMatchArray) => ParsedError;
}> = [
  // Content type constraint
  {
    pattern: /library_items_type_check/i,
    getMessage: () => ({
      message: 'Invalid content type selected. Please choose a valid type from the dropdown.',
      field: 'type',
      isRetryable: true
    })
  },
  // Character length violations - specific fields
  {
    pattern: /meta_title.*character varying\(60\)|value too long.*meta_title/i,
    getMessage: () => ({
      message: 'Meta title must be 60 characters or less.',
      field: 'meta_title',
      isRetryable: true
    })
  },
  {
    pattern: /meta_description.*character varying\(160\)|value too long.*meta_description/i,
    getMessage: () => ({
      message: 'Meta description must be 160 characters or less.',
      field: 'meta_description',
      isRetryable: true
    })
  },
  {
    pattern: /og_title.*character varying\(95\)|value too long.*og_title/i,
    getMessage: () => ({
      message: 'OG title must be 95 characters or less.',
      field: 'og_title',
      isRetryable: true
    })
  },
  {
    pattern: /og_description.*character varying\(300\)|value too long.*og_description/i,
    getMessage: () => ({
      message: 'OG description must be 300 characters or less.',
      field: 'og_description',
      isRetryable: true
    })
  },
  // Generic character length violation
  {
    pattern: /value too long for type character varying\((\d+)\)/i,
    getMessage: (match) => ({
      message: `Text is too long. Maximum ${match[1]} characters allowed.`,
      isRetryable: true
    })
  },
  // RLS violations
  {
    pattern: /row.level security|permission denied|insufficient privilege/i,
    getMessage: () => ({
      message: "You don't have permission to perform this action. Please sign in or contact an admin.",
      isRetryable: false
    })
  },
  // Unique constraint violations
  {
    pattern: /duplicate key.*unique constraint.*"([^"]+)"/i,
    getMessage: (match) => {
      const constraint = match[1];
      if (constraint.includes('email')) {
        return {
          message: 'This email address is already registered.',
          field: 'email',
          isRetryable: true
        };
      }
      if (constraint.includes('slug')) {
        return {
          message: 'A resource with this title already exists. Please use a different title.',
          field: 'title',
          isRetryable: true
        };
      }
      return {
        message: 'A record with these details already exists.',
        isRetryable: true
      };
    }
  },
  // Foreign key violations
  {
    pattern: /foreign key constraint.*"([^"]+)"/i,
    getMessage: () => ({
      message: 'Referenced item not found. It may have been deleted.',
      isRetryable: false
    })
  },
  // Not null violations
  {
    pattern: /null value in column "([^"]+)" violates not-null constraint/i,
    getMessage: (match) => ({
      message: `${formatFieldName(match[1])} is required.`,
      field: match[1],
      isRetryable: true
    })
  },
  // Check constraint violations (generic)
  {
    pattern: /violates check constraint "([^"]+)"/i,
    getMessage: () => ({
      message: 'Invalid value provided. Please check your input and try again.',
      isRetryable: true
    })
  }
];

// Network and API error patterns
const API_ERROR_PATTERNS: Array<{
  pattern: RegExp;
  getMessage: () => ParsedError;
}> = [
  {
    pattern: /network|fetch|timeout|ECONNREFUSED/i,
    getMessage: () => ({
      message: 'Connection error. Please check your internet and try again.',
      isRetryable: true
    })
  },
  {
    pattern: /401|unauthorized/i,
    getMessage: () => ({
      message: 'Your session has expired. Please sign in again.',
      isRetryable: false
    })
  },
  {
    pattern: /403|forbidden/i,
    getMessage: () => ({
      message: "You don't have permission to perform this action.",
      isRetryable: false
    })
  },
  {
    pattern: /404|not found/i,
    getMessage: () => ({
      message: 'The requested item was not found. It may have been deleted.',
      isRetryable: false
    })
  },
  {
    pattern: /429|rate limit|too many requests/i,
    getMessage: () => ({
      message: 'Too many requests. Please wait a moment and try again.',
      isRetryable: true
    })
  },
  {
    pattern: /500|internal server error/i,
    getMessage: () => ({
      message: 'Server error. Our team has been notified. Please try again later.',
      isRetryable: true
    })
  },
  {
    pattern: /502|bad gateway|503|service unavailable/i,
    getMessage: () => ({
      message: 'Service temporarily unavailable. Please try again in a few moments.',
      isRetryable: true
    })
  }
];

/**
 * Parse error with full details
 */
export function parseError(error: unknown): ParsedError {
  // Handle null/undefined
  if (!error) {
    return {
      message: 'An unexpected error occurred. Please try again.',
      isRetryable: true
    };
  }

  // Extract error message string
  let errorString = '';
  
  if (typeof error === 'string') {
    errorString = error;
  } else if (error instanceof Error) {
    errorString = error.message;
  } else if (typeof error === 'object') {
    const errorObj = error as Record<string, unknown>;
    errorString = String(
      errorObj.message || 
      errorObj.error || 
      errorObj.error_description ||
      errorObj.details ||
      JSON.stringify(error)
    );
  }

  // Try to match database error patterns first
  for (const { pattern, getMessage } of DB_ERROR_PATTERNS) {
    const match = errorString.match(pattern);
    if (match) {
      return getMessage(match);
    }
  }

  // Try API error patterns
  for (const { pattern, getMessage } of API_ERROR_PATTERNS) {
    if (pattern.test(errorString)) {
      return getMessage();
    }
  }

  // Default fallback
  return {
    message: 'Something went wrong. Please try again or contact support if the problem persists.',
    isRetryable: true
  };
}

/**
 * Parse any error into a user-friendly message
 */
export function parseErrorMessage(error: unknown): string {
  const parsed = parseError(error);
  return parsed.message;
}

/**
 * Get field-specific error message if available
 */
export function getFieldError(error: unknown, fieldName: string): string | null {
  const parsed = parseError(error);
  if (parsed.field === fieldName) {
    return parsed.message;
  }
  return null;
}
